import { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase/client';
import { collection, addDoc, serverTimestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import EmailService from '../services/EmailService';

const PaymentContext = createContext();

export function useRazorpay() {
  return useContext(PaymentContext);
}

export function RazorpayProvider({ children }) {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentPayment, setCurrentPayment] = useState(null);
  const [razorpayInstance, setRazorpayInstance] = useState(null);
  
  // Load Razorpay script
  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = () => {
          resolve(true);
        };
        script.onerror = () => {
          resolve(false);
        };
        document.body.appendChild(script);
      });
    };

    const initializeRazorpay = async () => {
      const res = await loadRazorpayScript();
      if (res) {
        console.log('Razorpay SDK loaded successfully');
      } else {
        console.error('Razorpay SDK failed to load');
      }
    };

    initializeRazorpay();
  }, []);
  
  // Create a Razorpay order (for future use when backend is available)
  const createRazorpayOrder = async (amount, receipt, notes = {}) => {
    try {
      // This function requires a backend API and cannot be used from the frontend directly
      // due to CORS and security restrictions
      throw new Error('Backend API required for creating Razorpay orders');
    } catch (error) {
      console.error('Error creating Razorpay order:', error);
      throw error;
    }
  };
  
  // Alternative direct payment approach
  const createPayment = async ({
    amount,
    bookingData,
    userId,
    onSuccess,
    onError
  }) => {
    try {
      // Log payment information for debugging
      console.log('PAYMENT AMOUNT CHECK:', {
        amount: amount,
        isFixed: amount === 500,
        isCOD: bookingData.paymentMethod === 'cod',
        displayAmount: `₹${amount.toLocaleString()}`
      });
      
      console.log('Starting payment process for:', {
        amount,
        bookingId: bookingData.id,
        paymentMethod: bookingData.paymentMethod,
        extraInfo: bookingData.advancePayment ? 'COD with advance payment' : 'Full payment'
      });
      
      // We already have a booking document with ID from bookingData.id
      const bookingId = bookingData.id;
      
      // Create a simplified payment object
      const paymentData = {
        amount: amount,
        bookingId: bookingId,
        userId: userId,
        timeCreated: new Date().toISOString()
      };
      
      // Store current payment for later use
      setCurrentPayment({
        paymentData,
        bookingData,
        bookingId,
        userId,
        onSuccess,
        onError
      });
      
      // Function to open Razorpay directly without creating an order first
      const openRazorpayCheckout = () => {
        if (!window.Razorpay) {
          onError('Razorpay SDK failed to load');
          return;
        }
        
        // Get Razorpay key from environment variables 
        const key = process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_live_oLhlEB8GqBvDZF"; 
        console.log("Using Razorpay Key:", key);
        
        // Generate a receipt ID (for reference only)
        const receiptId = `booking-${bookingId}-${Date.now()}`;
        
        // Create options for Razorpay - NOTE: no order_id is needed for direct payment
        const options = {
          key: key,
          amount: amount * 100, // Razorpay expects amount in paise
          currency: 'INR',
          name: 'Theatre Booking',
          description: bookingData.eventName || 'Ticket Booking',
          // No order_id used here - we're using direct payment
          receipt: receiptId,
          prefill: {
            name: bookingData.customerName || '',
            email: bookingData.email || '',
            contact: bookingData.phone || ''
          },
          notes: {
            booking_id: bookingId,
            receipt: receiptId
          },
          theme: {
            color: '#3399cc'
          },
          handler: function(response) {
            // Store payment data in a variable for use in the handler
            const payment = {
              paymentData,
              bookingData,
              bookingId,
              userId,
              onSuccess,
              onError,
              receiptId
            };
            
            // This function will be called when payment is successful
            console.log("Payment successful:", response);
            // Make sure we have the expected response format
            if (response && response.razorpay_payment_id) {
              // Pass the payment data to the handler
              processRazorpaySuccess(response, payment);
            } else {
              console.error("Invalid payment response:", response);
              processRazorpayFailure({ description: "Invalid payment response received" }, payment);
            }
          },
          modal: {
            ondismiss: function() {
              console.log("Payment window dismissed");
              // Store the current payment data for use in the failure handler
              const payment = {
                paymentData,
                bookingData,
                bookingId,
                userId,
                onSuccess,
                onError,
                receiptId
              };
              // User closed the payment window, treat as a payment failure
              processRazorpayFailure({ description: "Payment cancelled by user" }, payment);
            },
            escape: false,
            confirm_close: true
          }
        };
        
        // Create new Razorpay instance
        const razorpay = new window.Razorpay(options);
        
        // On payment failure
        razorpay.on('payment.failed', function(response) {
          // Store the current payment data for use in the failure handler
          const payment = {
            paymentData,
            bookingData,
            bookingId,
            userId,
            onSuccess,
            onError,
            receiptId
          };
          processRazorpayFailure(response.error, payment);
        });
        
        // Open Razorpay checkout
        razorpay.open();
      };
      
      // Open Razorpay checkout directly without order creation
      openRazorpayCheckout();
      
    } catch (error) {
      console.error('Payment process error:', error);
      onError(`Payment process failed: ${error.message || 'Unknown error'}`);
    }
  };
  
  // Define new functions that don't rely on currentPayment state
  const processRazorpaySuccess = async (response, payment) => {
    try {
      if (!payment) {
        console.error('No payment data provided to processRazorpaySuccess');
        return;
      }
      
      const { bookingId, userId, bookingData, onSuccess, receiptId } = payment;
      
      console.log('Processing payment success:', {
        bookingId,
        userId,
        amount: payment.paymentData ? payment.paymentData.amount : 'N/A',
        razorpayResponse: response,
        receiptId
      });
      
      // Determine if this is a COD booking with advance payment
      const isCODWithAdvance = bookingData && 
        bookingData.paymentMethod === 'cod' && 
        bookingData.advancePayment;
      
      // Prepare the update object based on payment type
      const updateData = {
        status: 'confirmed',
        updatedAt: serverTimestamp(),
        paymentId: response.razorpay_payment_id,
        receiptId: receiptId
      };
      
      if (isCODWithAdvance) {
        // For COD with advance payment
        updateData.advancePaymentId = response.razorpay_payment_id;
        updateData.advancePaymentDate = serverTimestamp();
        updateData.paymentStatus = 'partial';
        updateData.advancePayment = bookingData.advancePayment;
        updateData.balanceAmount = bookingData.balanceAmount;
      } else {
        // For full payment
        updateData.paymentId = response.razorpay_payment_id;
        updateData.paymentDate = serverTimestamp();
        updateData.paymentStatus = 'completed';
      }
      
      // Update booking with payment details
      await updateDoc(doc(db, 'bookings', bookingId), updateData);
      
      console.log('Booking updated with payment details', updateData);
      
      // Try updating the user's booking copy too
      try {
        if (userId) {
          await updateDoc(doc(db, 'users', userId, 'bookings', bookingId), updateData);
          console.log('User booking copy updated');
        }
      } catch (userBookingError) {
        console.error('Error updating user booking copy:', userBookingError);
      }
      
      // Reset payment state
      setCurrentPayment(null);
      
      // Call the success callback
      if (typeof onSuccess === 'function') {
        onSuccess(response);
      } else {
        console.error('onSuccess callback is not a function');
      }
    } catch (error) {
      console.error('Error completing payment:', error);
      if (payment && payment.onError) {
        payment.onError(error.message || 'Error processing payment');
      }
    }
  };
  
  const processRazorpayFailure = (error, payment) => {
    try {
      const errorMessage = error.description || error.message || 'Payment failed';
      console.error('Payment failure details:', {
        error: error,
        errorMessage: errorMessage,
        payment: payment ? {
          bookingId: payment.bookingId,
          amount: payment.paymentData ? payment.paymentData.amount : 'N/A',
          receiptId: payment.receiptId
        } : 'No payment data'
      });
      
      if (!payment) {
        console.error('No payment data provided to processRazorpayFailure');
        return;
      }
      
      const { bookingId, userId, onError } = payment;
      
      // Mark the booking as failed instead of deleting it
      try {
        // Update main booking record
        const updateData = {
          status: 'failed',
          updatedAt: serverTimestamp(),
          paymentStatus: 'failed',
          paymentError: errorMessage
        };
        
        // Update the booking in Firestore
        updateDoc(doc(db, 'bookings', bookingId), updateData).then(() => {
          console.log('Booking marked as failed:', bookingId);
        });
        
        // If there's a user copy, update that too
        if (userId && userId !== 'guest') {
          updateDoc(doc(db, 'users', userId, 'bookings', bookingId), updateData).then(() => {
            console.log('User booking copy marked as failed');
          });
        }
      } catch (updateError) {
        console.error('Error updating booking status on payment failure:', updateError);
      }
      
      // Reset payment state
      setCurrentPayment(null);
      
      // Call the error callback
      if (typeof onError === 'function') {
        onError(errorMessage);
      } else {
        console.error('onError callback is not a function');
      }
    } catch (handlerError) {
      console.error('Error in processRazorpayFailure:', handlerError);
    }
  };
  
  // Keep the original handlers for backward compatibility
  const handleRazorpaySuccess = async (response) => {
    try {
      if (!currentPayment) {
        console.error('No current payment found when handling payment success');
        return;
      }
      
      await processRazorpaySuccess(response, currentPayment);
    } catch (error) {
      console.error('Error in handleRazorpaySuccess:', error);
      handleRazorpayFailure(error);
    }
  };
  
  const handleRazorpayFailure = (error) => {
    try {
      if (!currentPayment) {
        console.error('No current payment found when handling payment failure');
        return;
      }
      
      processRazorpayFailure(error, currentPayment);
    } catch (handlerError) {
      console.error('Error in handleRazorpayFailure:', handlerError);
    }
  };

  const value = {
    createPayment,
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
} 